import React, { CSSProperties, FunctionComponent, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Responsive, isMobileDevice } from 'rs-emd-ui-atoms';
import { HTMLDivElement, Promise } from 'window-or-global';
import { PartsListContext } from '../../../../../components/app/parts-list-context';
import { useUserDispatch } from '../../../../../components/app/user-context';
import { getLabel } from '../../../helpers/html.utils';
import { isUnavailableProduct } from '../../../helpers/stock.utils';
import { getPrimaryImageUrl } from '../../../models/pdp/pdp-media.model';
import { PdpStickerContenType, PdpStickerPosition } from '../../../models/pdp/pdp-stickers.model';
import { AddToBasketCTA, ConfirmationModal } from '../../shared';
import { Action, PriceAreaActionType, PriceAreaProps, State } from './price-area-component.model';
import styles from './price-area-component.module.scss';

export const PriceArea: FunctionComponent<PriceAreaProps> = (props: PriceAreaProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isOutOfViewport, setIsOutOfViewport] = useState(false);
  const [onLoadDistFromTop, setOnLoadDistFromTop] = useState(0);
  const fixedCTARef = useRef<HTMLDivElement>(null);

  const { setPartsListProps } = useContext(PartsListContext);
  const userDispatch = useUserDispatch();

  const { search } = useLocation();

  const [state, dispatch] = useReducer(reducer, {
    quantity: props.priceArea.minimumOrderQuantity,
    partLists: {
      showConfirmation: false,
      listCreated: false,
    },
    showAddedToCart: false,
    isDropdownOpen: false,
    isEnergyModalOpen: false,
    isInBasket: props.priceArea.isInBasket,
  });

  //set the initial distance of the price area wrapper from the top of the window
  useEffect(() => {
    if (onLoadDistFromTop === 0 && ref?.current) setOnLoadDistFromTop(ref.current.getBoundingClientRect().top);
  }, [onLoadDistFromTop, ref]);

  let onAddClick = useCallback(
    (id?: number, name?: string, desc?: string) => {
      dispatch({ type: PriceAreaActionType.AddToPartList, id, name, desc });

      //close parts lists modal
      setPartsListProps(undefined);
    },
    [setPartsListProps]
  );

  //open parts list modal with pre-selected product if stockcode in url is the same as product's stockcode
  useEffect(() => {
    const searchParams = new URLSearchParams(search);
    const partsListProduct = searchParams.get('partsListProduct')?.split(';');

    if (partsListProduct && partsListProduct[0] === props.titleArea?.stockCode) {
      dispatch({ type: PriceAreaActionType.SetPartsListData });

      setPartsListProps({
        products: [{ stockCode: partsListProduct[0], quantity: Number(partsListProduct[1]) }],
        onAddClick: onAddClick,
      });
    }
  }, [onAddClick, props.titleArea, search, setPartsListProps]);

  useEffect(() => {
    const handleScroll = () => {
      if (ref?.current && onLoadDistFromTop !== 0) {
        // cannot use window.scrollY since overflow-x: hidden on the body causes it to stop working
        setIsOutOfViewport(document.body.scrollTop > onLoadDistFromTop + ref.current.clientHeight);
      }
    };

    document.body.addEventListener('scroll', handleScroll);

    return () => {
      document.body.removeEventListener('scroll', () => handleScroll);
    };
  }, [onLoadDistFromTop, ref]);

  return (
    <div ref={ref} className={styles['pricing-container-wrapper']}>
      {/* Add to basket */}
      {(!props.stockArea?.isUsingDTP || isMobileDevice() || !isUnavailableProduct(props.stockArea?.statusCode)) && getPricingContainer()}

      {/* Add to basket - FIXED CTA*/}
      {(isMobileDevice() || !isUnavailableProduct(props.stockArea?.statusCode)) && (
        <div
          ref={fixedCTARef}
          style={{ '--fixed-cta-height': fixedCTARef.current?.clientHeight + 'px' } as CSSProperties}
          className={`${styles['fixed-cta']} ${
            isOutOfViewport
              ? `${styles.fixed} ${state.isDropdownOpen ? styles.top : ''} ${
                  state.isEnergyModalOpen ? styles['fixed-cta-energy-modal-visible'] : ''
                }`
              : styles.hide
          } `}>
          <div className={`inner-page-wrapper ${styles['fixed-cta-content']}`}>
            <Responsive breakpoints={['tablet', 'desktop', 'lg-desktop', 'xl-desktop']}>{getMediaThumbnail()}</Responsive>
            {getPricingContainer(true)}
          </div>
        </div>
      )}

      {/* Added to Cart &  Parts list - confirmation modal */}
      {(state.showAddedToCart || state.partLists.showConfirmation) && (
        <ConfirmationModal
          labels={props.labels}
          confirmationModalTitle={
            state.showAddedToCart
              ? getLabel(props.labels, 'added_to_basket')
              : getLabel(props.labels, state.partLists.listCreated ? 'list_created_product_added' : 'product_added_to_list')
          }
          title={props.titleArea?.title}
          details={
            state.showAddedToCart && (
              <p className={`snippet ${styles.quantity}`}>
                {getLabel(props.labels, 'quantity_added').replace('[quantity]', state.quantity.toString())}
              </p>
            )
          }
          mediaArea={props.mediaArea}
          viewButtonHref={state.showAddedToCart ? '/basket' : ''}
          backButtonText='continue_shopping'
          viewButtonCallback={state.showAddedToCart ? undefined : () => onAddToPartsListClick(true)}
          viewButtonText={state.showAddedToCart ? getLabel(props.labels, 'view_basket') : getLabel(props.labels, 'view_list')}
          closeCallback={() => dispatch({ type: PriceAreaActionType.CloseConfirmationMessage })}
        />
      )}
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case PriceAreaActionType.SetPartsListData: {
        return {
          ...state,
          partLists: {
            ...state.partLists,
            showConfirmation: false,
            listId: !action.showSelectedList ? undefined : state.partLists.listId,
          },
        };
      }
      case PriceAreaActionType.AddToPartList: {
        return {
          ...state,
          partLists: {
            listId: action.id,
            showConfirmation: true,
            listCreated: action.name ? true : false,
          },
        };
      }
      case PriceAreaActionType.AddedToCart: {
        return {
          ...state,
          isDropdownOpen: false,
          showAddedToCart: true,
          isInBasket: true,
        };
      }
      case PriceAreaActionType.CloseConfirmationMessage: {
        return {
          ...state,
          partLists: {
            ...state.partLists,
            showConfirmation: false,
          },
          showAddedToCart: false,
        };
      }
      case PriceAreaActionType.ToggleDropdownOpen: {
        return {
          ...state,
          isDropdownOpen: action.isOpen,
        };
      }
      case PriceAreaActionType.QtyChanged: {
        return {
          ...state,
          quantity: action.qty,
        };
      }
      case PriceAreaActionType.SetIsEnergyModalOpen: {
        return {
          ...state,
          isEnergyModalOpen: action.isOpen,
        };
      }
    }
  }

  function getPricingContainer(isInFixedCTA?: boolean) {
    return (
      <Card className={styles['pricing-container']} squareCorners={true}>
        {props.titleArea?.stockCode && (
          <AddToBasketCTA
            labels={props.labels}
            allowAddToPartsList={props.allowAddToPartsList}
            titleArea={{ title: props.titleArea?.title, stockCode: props.titleArea?.stockCode }}
            priceArea={props.priceArea}
            ppkArea={props.ppkArea}
            calibrationArea={props.calibrationArea}
            onCalibrationChange={props.onCalibrationChange}
            onPackagingChange={props.onPackagingChange}
            stockArea={props.stockArea}
            pricingService={props.pricingService}
            isBarred={props.punchoutInfoArea.isBarred}
            isDualUse={false} //TODO: get data
            isInFixedCTA={isInFixedCTA}
            selectedPriceBreakIndex={props.selectedPriceBreakIndex}
            mediaArea={props.mediaArea}
            energyArea={props.energyArea}
            locale={props.locale}
            productService={props.productService}
            onQtyChange={onQtyChange}
            onAddToBasketClick={onAddToBasketClick}
            onAddToPartsListClick={() => onAddToPartsListClick(false)}
            onQtyDropdownClick={(isOpen: boolean) => {
              dispatch({ type: PriceAreaActionType.ToggleDropdownOpen, isOpen: isOpen });
            }}
            onEnergyModalToggle={(isOpen) => dispatch({ type: PriceAreaActionType.SetIsEnergyModalOpen, isOpen: isOpen })}
            euElrProductInformationSheet={props.euElrProductInformationSheet}
            onUnavailableEnergyProductInfoSheetBtnClick={props.onUnavailableEnergyProductInfoSheetBtnClick}
            isInBasket={state.isInBasket}
          />
        )}
        {/* CMS Content */}
        {getSticker(PdpStickerPosition.DetailsSection)}
        {getSticker(PdpStickerPosition.DetailsSectionBottom)}
      </Card>
    );
  }

  function getSticker(position: PdpStickerPosition) {
    var sticker = props.stickersArea.stickers.find((s) => s.position === position);
    if (sticker) {
      if (sticker.contentType === PdpStickerContenType.Html)
        return <div className={styles['cms-content']} dangerouslySetInnerHTML={{ __html: sticker.content }}></div>;
      else if (sticker.contentType === PdpStickerContenType.ImageUrl)
        return (
          <div className={styles['cms-content']}>
            <img src={sticker.content} alt={`sticker-${sticker.id}`}></img>
          </div>
        );
    }
  }

  function getMediaThumbnail() {
    if (props.mediaArea) {
      let mediaItem = getPrimaryImageUrl(props.mediaArea, undefined, 'medium');
      if (mediaItem) {
        return (
          <div className={styles.thumbnail}>
            <img id={`add-thumbnail-${mediaItem.id}`} src={mediaItem.url} alt={`thumbnail-${mediaItem.name}`} />
          </div>
        );
      }
    }
  }

  async function onAddToBasketClick(qty: number): Promise<void> {
    if (props.titleArea?.stockCode) {
      dispatch({ type: PriceAreaActionType.CloseConfirmationMessage });

      const res = await props.basketService.addToBasket(
        {
          variables: {
            stockCode: props.titleArea.stockCode,
            quantity: qty,
          },
          actionType: 'product',
        },
        userDispatch
      );
      if (res.data?.addToBasketV2) {
        dispatch({ type: PriceAreaActionType.AddedToCart });
      }
    }
    return Promise.resolve();
  }

  function onQtyChange(quantity?: number) {
    if (quantity) dispatch({ type: PriceAreaActionType.QtyChanged, qty: quantity });
    props.onQtyChange(quantity);
  }

  function onAddToPartsListClick(showSelectedList?: boolean) {
    dispatch({ type: PriceAreaActionType.SetPartsListData, showSelectedList: showSelectedList });

    setPartsListProps({
      products: [{ stockCode: props.titleArea?.stockCode ?? '', quantity: state.quantity }],
      newListId: showSelectedList ? state.partLists.listId : undefined,
      onAddClick: onAddClick,
    });
  }
};

export default PriceArea;
